/**
 * AppShell.tsx — the authenticated portal layout: sidebar + main content.
 *
 * Pure presentational shell (no MSAL coupling) so it is testable in isolation.
 * `userMenu` is an optional slot (top-right of the header row) for the
 * MSAL-coupled account/sign-out control — App.tsx supplies it so this
 * component itself never touches MSAL.
 * It renders the active panel in `children` and owns the cockpit's global
 * KEYBOARD SHORTCUTS for two power-users who live in this tool all day:
 *
 *   g h / g l / g c  → switch to Health / Leads / Conversations (hash nav)
 *   r                → refresh the current panel (dispatches a `tcn:refresh`
 *                      window event the panels listen for)
 *   ?                → toggle the shortcut legend;  Esc closes it
 *
 * Shortcuts are suppressed while a text field / select is focused and while any
 * modifier (Ctrl/Cmd/Alt) is held, so they never hijack typing. The "?" legend
 * (bottom-right) documents them for discoverability.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Sidebar } from './Sidebar';
import { ShortcutLegend } from './ShortcutLegend';

export interface AppShellProps {
  activeId?: string;
  children: ReactNode;
  userMenu?: ReactNode;
}

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
};

const COLLAPSE_KEY = 'tcn.nav.collapsed';
const readCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
};
const isProductsHash = () => window.location.hash.startsWith('#numbers/products');

const GOTO_KEYS: Record<string, string> = { h: 'health', l: 'leads', c: 'conversations' };

export const AppShell = ({
  activeId = 'health',
  children,
  userMenu = undefined,
}: AppShellProps) => {
  const { t } = useTranslation();
  const [legendOpen, setLegendOpen] = useState(false);
  const legendId = useId();
  const navId = useId();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [onProducts, setOnProducts] = useState(isProductsHash);
  // Whether a `g` prefix is armed (GitHub-style two-key nav), with a short TTL.
  const gotoArmed = useRef(false);
  const gotoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      window.localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
    } catch {
      // storage unavailable: the rail state just doesn't persist
    }
  };

  // Products shares the `numbers` panel; the hash tells the two nav entries apart.
  useEffect(() => {
    const onHash = () => {
      setDrawerOpen(false);
      setOnProducts(isProductsHash());
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  useEffect(() => {
    const disarm = () => {
      gotoArmed.current = false;
      if (gotoTimer.current != null) {
        clearTimeout(gotoTimer.current);
        gotoTimer.current = null;
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isEditableTarget(event.target)) return;

      if (event.key === 'Escape') {
        setLegendOpen(false);
        disarm();
        return;
      }
      if (event.key === '?') {
        setLegendOpen((open) => !open);
        return;
      }

      if (gotoArmed.current) {
        const dest = GOTO_KEYS[event.key];
        disarm();
        if (dest) {
          event.preventDefault();
          window.location.hash = `#${dest}`;
        }
        return;
      }

      if (event.key === 'g') {
        gotoArmed.current = true;
        gotoTimer.current = setTimeout(disarm, 1500);
        return;
      }
      if (event.key === 'r') {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent('tcn:refresh'));
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      disarm();
    };
  }, []);

  const navActive = activeId === 'numbers' && onProducts ? 'products' : activeId;
  const menuBtn =
    'inline-flex h-11 w-11 items-center justify-center rounded-md text-ink-1 ring-1 ring-inset ring-control hover:bg-surface-2';
  return (
    <div className="flex min-h-screen bg-canvas text-ink-1">
      <a href="#main-content" className="skip-link">
        {t('app.skipToContent')}
      </a>
      <Sidebar id={navId} activeId={navActive} collapsed={collapsed} open={drawerOpen} />
      {drawerOpen ? (
        <button
          type="button"
          tabIndex={-1}
          aria-label={t('shell.close')}
          data-testid="nav-backdrop"
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 z-30 bg-canvas/70 md:hidden"
        />
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-outline bg-surface-nav px-4 py-2 md:px-6">
          <button
            type="button"
            data-testid="nav-drawer-toggle"
            aria-expanded={drawerOpen}
            aria-controls={navId}
            aria-label={t('shell.menu')}
            onClick={() => setDrawerOpen((open) => !open)}
            className={`${menuBtn} md:hidden`}
          >
            <span aria-hidden="true">☰</span>
          </button>
          <button
            type="button"
            data-testid="nav-collapse-toggle"
            aria-expanded={!collapsed}
            aria-controls={navId}
            aria-label={collapsed ? t('shell.expand') : t('shell.collapse')}
            onClick={toggleCollapsed}
            className={`${menuBtn} hidden md:inline-flex`}
          >
            <span aria-hidden="true">☰</span>
          </button>
          <span
            data-testid="topbar-context"
            className="min-w-0 flex-1 truncate text-body text-ink-2"
          >
            {t(`nav.${navActive}`)}
          </span>
          {userMenu}
        </header>
        <main
          id="main-content"
          className={`flex-1 p-4 md:p-6 ${
            activeId === 'conversations' ? '' : 'mx-auto w-full max-w-7xl'
          }`}
        >
          {children}
        </main>
      </div>
      <ShortcutLegend
        open={legendOpen}
        onToggle={() => setLegendOpen((open) => !open)}
        panelId={legendId}
      />
    </div>
  );
};
